import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { APP_CONFIG } from '../../services/config/app-config.token';
import { AppConfig } from '../../services/config/config.model';

export interface HealthCheckResult {
    ok: boolean;
    httpStatus?: number;
    statusText?: string;
    body?: unknown;
    error?: unknown;
}

@Injectable({ providedIn: 'root' })
export class HealthService {
    private http = inject(HttpClient);
    private config: AppConfig = inject(APP_CONFIG);
    // Build a robust base URL:
    // - If dsiMessagingServiceUrl is '' => '/api/v1'
    // - If it's '/api' => '/api/v1'
    // - If it's a host like 'https://host' => 'https://host/api/v1'
    private baseUrl = (() => {
        const root = (this.config.dsiMessagingServiceUrl || '').replace(/\/+$/, '');
        if (root === '' || root === '/api') {
            return '/api/v1';
        }
        return `${root}/api/v1`;
    })();

    public check(): Observable<HealthCheckResult> {
        return this.http.get(`${this.baseUrl}/Health/Check`, { observe: 'response' }).pipe(
            map((resp: HttpResponse<unknown>) => {
                const contentType = resp.headers?.get('content-type') || '';
                // If we got HTML back, it's probably the SPA index.html (proxy or path issue)
                if (contentType.includes('text/html')) {
                    return {
                        ok: false,
                        httpStatus: resp.status,
                        statusText: 'Unhealthy',
                        body: resp.body,
                        error: 'Received HTML instead of JSON from health endpoint (check local proxy / base URL)',
                    } as HealthCheckResult;
                }
                return {
                    ok: resp.status === 200,
                    httpStatus: resp.status,
                    statusText: resp.status === 200 ? 'Healthy' : resp.statusText || 'Unhealthy',
                    body: resp.body,
                } as HealthCheckResult;
            }),
            catchError((err: HttpErrorResponse) =>
                of({
                    ok: false,
                    httpStatus: err.status,
                    statusText: 'Unhealthy',
                    error: err.error ?? err.message,
                })
            )
        );
    }
}
